// Small DOM helpers shared by every view.
import { store } from './store';

export const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;

export function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string | number): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = String(text);
  return e;
}

export function td(text: string | number, cls?: string): HTMLTableCellElement {
  return el('td', cls, text);
}

/** CSS colour for a PM: a variable the app defines from the pms table, so dark mode works. */
export function pmColour(pm: string): string {
  return store.pms.some((p) => p.name === pm) ? `var(--pm-${cssName(pm)})` : 'var(--muted)';
}
export const cssName = (pm: string) => pm.toLowerCase().replace(/[^a-z0-9]+/g, '-');

/**
 * Fill a [data-pmseg] group with All + one button per PM, keeping the pressed state.
 * `counts` adds "(n)" after each name, as the To do tab does.
 */
export function pmSeg(box: HTMLElement, current: string, onPick: (pm: string) => void, counts?: Record<string, number>) {
  const names = ['', ...store.pms.filter((p) => p.active).map((p) => p.name)];
  const sig = names.join('|');
  if (box.dataset.sig !== sig) {
    box.replaceChildren();
    names.forEach((n) => {
      const b = el('button');
      b.type = 'button'; b.dataset.pm = n;
      b.append(n || 'All');
      if (counts && n) { const s = el('span'); s.dataset.n = n; b.append(' ', s); }
      b.addEventListener('click', () => onPick(n));
      box.append(b);
    });
    box.dataset.sig = sig;
  }
  box.querySelectorAll<HTMLButtonElement>('button').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.pm === current)));
  if (counts) box.querySelectorAll<HTMLSpanElement>('[data-n]').forEach((s) => { s.textContent = '(' + (counts[s.dataset.n!] || 0) + ')'; });
}

/** Bind a static segmented control (data-<attr> buttons). */
export function segBind(id: string, attr: string, fnSet: (v: string) => void) {
  document.querySelectorAll<HTMLButtonElement>('#' + id + ' button').forEach((b) => b.addEventListener('click', () => {
    document.querySelectorAll('#' + id + ' button').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
    fnSet(b.dataset[attr] || '');
  }));
}

let toastT: ReturnType<typeof setTimeout> | undefined;
export function toast(msg: string) {
  const t = $('toast');
  t.textContent = msg; t.hidden = false;
  clearTimeout(toastT);
  toastT = setTimeout(() => { t.hidden = true; }, 1600);
}

/** Stop clicks on a control inside a <summary> from opening or closing the card. */
export function guard(e: HTMLElement) {
  (['click', 'mousedown', 'pointerdown'] as const).forEach((ev) => e.addEventListener(ev, (x) => x.stopPropagation()));
  e.addEventListener('keydown', (x) => { if (x.key === ' ' || x.key === 'Enter') x.stopPropagation(); });
}

/** A button that needs a second tap within `ms` to fire. Returns the arm/disarm logic bound to `b`. */
export function twoTap(b: HTMLButtonElement, armedText: string, onConfirm: () => void, ms = 3000, armedStyle?: (on: boolean) => void) {
  const idle = b.textContent;
  let armed = false;
  b.addEventListener('click', () => {
    if (!armed) {
      armed = true; b.textContent = armedText; armedStyle?.(true);
      setTimeout(() => { armed = false; b.textContent = idle; armedStyle?.(false); }, ms);
      return;
    }
    onConfirm();
  });
}
